import React from "react";
import { Dices } from "lucide-react";
import { StudioToolParam } from "../../service/apiService";

interface ParamFieldProps {
  param: StudioToolParam;
  value: any;
  onChange: (value: any) => void;
  onRandomize?: () => void;
}

const labelize = (name: string) =>
  name.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const inputBase =
  "w-full rounded-md bg-surface-2 border border-border-default px-3 py-2 text-sm text-primary placeholder:text-subtle focus:outline-none focus:ring-2 focus:ring-violet-500/60";

const ParamField: React.FC<ParamFieldProps> = ({ param, value, onChange, onRandomize }) => {
  const label = labelize(param.name);
  const isMultiline = param.type === "text" && ["prompt", "negative_prompt"].includes(param.name);

  // Prompt / negative prompt → textarea
  if (isMultiline) {
    return (
      <div className="space-y-1">
        <label className="block text-xs font-medium text-muted">
          {label}
          {param.required && <span className="text-violet-500"> *</span>}
        </label>
        <textarea
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          rows={param.name === "prompt" ? 3 : 2}
          placeholder={param.description || ""}
          className={`${inputBase} resize-none`}
        />
      </div>
    );
  }

  // Short text / string → input
  if (param.type === "text" || param.type === "string") {
    return (
      <div className="space-y-1">
        <label className="block text-xs font-medium text-muted">
          {label}
          {param.required && <span className="text-violet-500"> *</span>}
        </label>
        <input
          type="text"
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={param.description || ""}
          className={inputBase}
        />
      </div>
    );
  }

  // Enum → select (optional enums get a "Default" empty option)
  if (param.type === "enum") {
    const options = param.enum || [];
    return (
      <div className="space-y-1">
        <label className="block text-xs font-medium text-muted">
          {label}
          {param.required && <span className="text-violet-500"> *</span>}
        </label>
        <select
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          className={inputBase}
        >
          {!param.required && param.default == null && <option value="">Default</option>}
          {options.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      </div>
    );
  }

  // Boolean → checkbox
  if (param.type === "bool") {
    return (
      <label className="flex items-center gap-2 text-sm text-muted">
        <input
          type="checkbox"
          checked={!!value}
          onChange={(e) => onChange(e.target.checked)}
          className="h-4 w-4"
        />
        {label}
      </label>
    );
  }

  // Numbers (int / float)
  const isFloat = param.type === "float";
  const min = param.min ?? 0;
  const max = param.max ?? 100;
  const step = isFloat ? 0.05 : 1;
  const numValue = value ?? param.default ?? min;

  // Seed gets a plain number input + a randomize dice (huge range, slider useless)
  if (param.name === "seed") {
    return (
      <div className="space-y-1">
        <label className="block text-xs font-medium text-muted">{label}</label>
        <div className="flex items-center gap-2">
          <input
            type="number"
            value={value ?? 0}
            min={min}
            max={max}
            onChange={(e) => onChange(Number(e.target.value))}
            className={inputBase}
          />
          {onRandomize && (
            <button
              type="button"
              onClick={onRandomize}
              title="Randomize seed"
              className="shrink-0 rounded-md border border-border-default bg-surface-2 p-2 text-muted hover:text-primary hover:bg-surface-3"
            >
              <Dices size={16} />
            </button>
          )}
        </div>
      </div>
    );
  }

  // Bounded numbers → slider + readout
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <label className="text-xs font-medium text-muted">{label}</label>
        <span className="text-xs tabular-nums text-subtle">
          {isFloat ? Number(numValue).toFixed(2) : numValue}
        </span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={numValue}
        onChange={(e) => onChange(isFloat ? parseFloat(e.target.value) : parseInt(e.target.value, 10))}
        className="w-full accent-violet-500"
      />
    </div>
  );
};

export default ParamField;
